<script lang="ts" setup>
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import { WorkCycle } from '@/core/collections/contracts-api/customers/work-cycles/work-cycle.interface';
import GenericList from '@/core/components/templates/generic-list/GenericList.vue';
import { useGenericList } from '@/core/hooks/useGenericList';
import { useStore } from '@/core/hooks/useStore';
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { LocationQuery, useRoute } from 'vue-router';
import WorkCycleModal from './WorkCycleModal.vue';

//#region Globals
const { t } = useI18n();
const { state, dispatch } = useStore();
const { id: customerId } = useRoute().params;
const customer = inject('customer', ref<Customer>());
//#endregion

//#region Permissions
const permissions = computed(() => state.permission?.permissions);
//#endregion

//#region Generic List
const workCyclesOverview = computed(() => state.customer?.workCycle?.workCyclesOverview);
const genericList = useGenericList({
   routeName: 'ContractAndPerformanceDataWorkCycles',
   translation: (length = 0) => t('work-cycles.work-cycle', length),
   actions: {
      getAll: (query?: LocationQuery) => {
         return dispatch('customer/workCycle/getWorkCycles', { customerId, query: { ...query, append: 'scopes' } });
      },
      create: (workCycle: WorkCycle) => {
         return dispatch('customer/workCycle/createWorkCycle', { customerId, workCycle });
      },
      change: (workCycle: WorkCycle) => {
         return dispatch('customer/workCycle/changeWorkCycle', { customerId, workCycleId: workCycle.id, workCycle });
      },
      delete: (workCycleId: number) => {
         return dispatch('customer/workCycle/deleteWorkCycle', { customerId, workCycleId });
      },
   },
});

const { loading, loadingChange, loadingCreate } = genericList;
const { createData, changeData, deleteData, deleteMultipleData } = genericList;
//#endregion
</script>

<template>
   <GenericList
      :class="'contract-and-performance-data-work-cycles'"
      :translation="(length) => t('work-cycles.work-cycle', length)"
      :list-items="workCyclesOverview?.data || []"
      :loading="loading"
      :meta="workCyclesOverview"
      :mode="'light'"
      :name="'work-cycle'"
      :headings="[
         { label: t('generic.name'), style: { minWidth: '11.54rem' } },
         { label: t('work-cycles.worksheets'), style: { width: '100%' } },
         { label: '', style: { minWidth: '2.3rem' } },
      ]"
   >
      <!-- List Items -->
      <template #listItem="{ item: workCycle }: { item: WorkCycle }">
         <td>{{ workCycle.name }}</td>
         <td>{{ workCycle?.worksheets?.length }}</td>
         <td>
            <NxtIconButton
               v-if="$auth('delete', permissions?.customers)"
               :name="'trash'"
               :disabled="loading"
               :hover-text="$t('actions.delete-type', { type: $t('work-cycles.work-cycle') })"
               @on-click="deleteData({ itemToDelete: workCycle })"
            />
         </td>
      </template>

      <!-- Action Bar Actions -->
      <template v-if="$auth('delete', permissions?.customers)" #actionBarActions="{ deselectAllItems, selectedItems, count }">
         <NxtIconButton
            v-if="$auth('delete', permissions?.customers)"
            :name="'trash'"
            :disabled="loading"
            :hover-text="$t('actions.delete-type', { type: $t('work-cycles.work-cycle', count) })"
            @on-click="deleteMultipleData({ itemsToDelete: selectedItems, done: deselectAllItems })"
         />
      </template>

      <!-- Create work cycle -->
      <template v-if="$auth('create', state.permission?.permissions?.customers)" #createModal="{ closeCreateItemModal }">
         <WorkCycleModal
            :customer="customer"
            :loading="loadingCreate"
            @on-cancel="closeCreateItemModal"
            @on-save="createData($event, closeCreateItemModal)"
         />
      </template>

      <!-- Change Work Cycle -->
      <template #changeModal="{ item, closeChangeItemModal }">
         <WorkCycleModal
            :customer="customer"
            :initial-work-cycle="item"
            :loading="loadingChange"
            @on-cancel="closeChangeItemModal"
            @on-save="changeData($event, closeChangeItemModal)"
         />
      </template>
   </GenericList>
</template>
